/**
 * Thai bank registry for display.
 *
 * SAD §12's seed data names KBANK, SCB, BBL, KTB, BAY and TMB; the contracts deliberately keep
 * `BankAccount.bank` a free-form code rather than an enum so a participant can onboard a new
 * payee bank without a contracts release. This registry is therefore *presentation only*: an
 * unknown code still renders, just without a brand colour or a Thai name.
 */

/** Display metadata for a bank code. */
export interface BankInfo {
  /** The wire code, e.g. `KBANK`. */
  readonly code: string;
  /** English short name. */
  readonly name: string;
  /** Thai name, shown as a secondary line where there is room. */
  readonly nameTh: string;
  /** Brand colour as a CSS colour string, used for the avatar chip only. */
  readonly color: string;
  /** Two-or-three character monogram for the avatar chip. */
  readonly monogram: string;
}

const REGISTRY: Readonly<Record<string, BankInfo>> = {
  KBANK: {
    code: 'KBANK',
    name: 'Kasikornbank',
    nameTh: 'ธนาคารกสิกรไทย',
    color: '#0a8a41',
    monogram: 'K',
  },
  SCB: {
    code: 'SCB',
    name: 'Siam Commercial Bank',
    nameTh: 'ธนาคารไทยพาณิชย์',
    color: '#4d2a86',
    monogram: 'SCB',
  },
  BBL: {
    code: 'BBL',
    name: 'Bangkok Bank',
    nameTh: 'ธนาคารกรุงเทพ',
    color: '#1c4d9c',
    monogram: 'BBL',
  },
  KTB: {
    code: 'KTB',
    name: 'Krungthai Bank',
    nameTh: 'ธนาคารกรุงไทย',
    color: '#00a5e0',
    monogram: 'KTB',
  },
  BAY: {
    code: 'BAY',
    name: 'Krungsri',
    nameTh: 'ธนาคารกรุงศรีอยุธยา',
    color: '#a1a300',
    monogram: 'BAY',
  },
  TMB: {
    code: 'TMB',
    name: 'ttb',
    nameTh: 'ธนาคารทหารไทยธนชาต',
    color: '#0050a0',
    monogram: 'ttb',
  },
};

/** Every bank code the seed data and the payee-account admin screen use. */
export const BANK_CODES = Object.keys(REGISTRY);

/** Display metadata for `code`, synthesising a neutral entry for unknown codes. */
export function bankInfo(code: string): BankInfo {
  return (
    REGISTRY[code] ?? {
      code,
      name: code,
      nameTh: code,
      color: '#64748b',
      monogram: code.slice(0, 3).toUpperCase(),
    }
  );
}

/**
 * Mask an account number the way Thai banks do on a slip: `xxx-x-x5678-x`.
 *
 * Used when the console needs to *display* an unmasked number it received (it should not have
 * one, but defence in depth beats trust) and when rendering a payee for comparison.
 */
export function maskAccountNumber(accountNumber: string): string {
  const digits = accountNumber.replace(/\D/g, '');
  if (digits.length <= 4) return accountNumber;
  return `xxx-x-x${digits.slice(-5, -1)}-x`;
}

/**
 * Spellings of one bank → the code they are compared on. The display twin of
 * `packages/domain/src/verification/bank-code.ts` (the console cannot depend on `@pf/domain`) —
 * keep the two tables identical.
 */
const BANK_CODE_ALIASES: Readonly<Record<string, string>> = {
  TMB: 'TTB',
  CIMB: 'CIMBT',
  UOB: 'UOBT',
  ICBC: 'ICBCT',
  LHBANK: 'LHFG',
  LHB: 'LHFG',
  KK: 'KKP',

  IBANK: 'ISBT',
  GHBANK: 'GHB',
  TCRB: 'TCD',
  THAICREDIT: 'TCD',
  SMEBANK: 'SME',
  CITIBANK: 'CITI',
};

/**
 * Whether two bank codes name the same bank — what the ✓/✗ on a review screen has to agree with
 * PaymentFlow about. A slip's bank arrives translated from a clearing code (`011` → `TMB`) while
 * the website may have registered `TTB`; compared as strings the row showed ✗ on a check
 * PaymentFlow had passed, and the operator's only evidence said "wrong bank" on a correct slip.
 */
export function isSameBank(left: string, right: string): boolean {
  const canonical = (code: string): string => {
    const normalized = code.trim().toUpperCase();
    return BANK_CODE_ALIASES[normalized] ?? normalized;
  };
  const a = canonical(left);
  return a !== '' && a === canonical(right);
}

/**
 * Positional comparison of a bank's masked account against the full registered number — the
 * display twin of the domain's `account-matcher.ts` (the console cannot depend on `@pf/domain`).
 *
 * Each bank masks a different span (`xxx-x-x2901-x`, `XXX-X-XX513-5`, `xxx-x-x1630-x77`), so
 * comparing "last four visible digits" convicts honest slips. Returns `null` when the mask
 * cannot be aligned — unknown, not a mismatch.
 */
export function maskedAccountMatches(accountMasked: string, fullNumber: string): boolean | null {
  let pattern = '';
  for (const ch of accountMasked) {
    if (ch >= '0' && ch <= '9') pattern += ch;
    else if ('xX*•#'.includes(ch)) pattern += 'x';
  }
  const digits = fullNumber.replace(/\D/g, '');
  if (digits.length === 0 || !/[0-9]/.test(pattern)) return null;

  // Indexed rather than spread: `pattern` is built above from ASCII digits and `x` only, so
  // grapheme splitting is not a hazard here — but the lint rule cannot know that, and an inline
  // disable would outlive the loop that guarantees it.
  const agreesAt = (offset: number): boolean => {
    for (let i = 0; i < pattern.length; i += 1) {
      const ch = pattern[i];
      if (ch !== 'x' && ch !== digits[offset + i]) return false;
    }
    return true;
  };

  if (pattern.length === digits.length) return agreesAt(0);
  if (pattern.length < digits.length) {
    return agreesAt(digits.length - pattern.length) || agreesAt(0) ? true : null;
  }
  return null;
}

/** The trailing four digits of an account number, used for the SAD §6 last-4 check. */
export function lastFour(accountNumber: string): string {
  const digits = accountNumber.replace(/\D/g, '');
  return digits.slice(-4);
}
